/** @format */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LabelInput from "../../../shared/components/labelInput";
import Tq3Mapper from "../mappers/tq3Mapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import { handleNext } from "../../../shared/utils/navigationUtils";
import useQuoteStore from "../store/QuoteStore";

import {
  fetchEmp,
  fetchEmpBusiness,
  fetchMartialStatus,
  fetchOccupation,
} from "../../../shared/services/simpleListItemsService";
import NewDatePicker from "../../../shared/components/newDatePicker";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import RadioGroup from "../../../shared/components/RadioGroup";
import ModalWrapper from "../../../shared/components/ModalWrapper";
import FieldStack from "../../../shared/components/FieldStack";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";

const Tq3 = () => {
  // Store
  // Form Setup
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    setValue,
    trigger,
    handleSubmit,
  } = useForm({
    defaultValues: Tq3Mapper.get(),
  });

  const STATE = useAppStateStore();

  // State
  const [occupation, setOccupation] = useState([]);
  const [employeeBusiness, setEmployeeBusiness] = useState([]);
  const [employee, setEmployee] = useState([]);
  const [martial, setMartial] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDateLimitModalOpen, setIsDateLimitModalOpen] = useState(false);

  const prioritizedOccupations = ["Taxi Driver", "Taxi Operating", "Chauffeur"];
  useEffect(() => {
    if (watch("DriverResidecy") === "Yes") {
      const driverDOB = useQuoteStore.getState().quote?.risk?.drivers[0]
        ?.dateOfBirth;

      if (driverDOB) {
        const birthDate = new Date(driverDOB); // "1964-02-19T00:00:00" format will work directly with new Date()
        const today = new Date();

        let residentYears = today.getFullYear() - birthDate.getFullYear();

        // Adjust age if birthday hasn't occurred this year
        if (
          today.getMonth() < birthDate.getMonth() ||
          (today.getMonth() === birthDate.getMonth() &&
            today.getDate() < birthDate.getDate())
        ) {
          residentYears--;
        }

        setValue("DriverYears", residentYears);
      }
    }
  }, [watch("DriverResidecy")]);

  const prioritizeOccupations = (array) => {
    const priorityItems = [];
    const otherItems = [];

    array.forEach((item) => {
      if (prioritizedOccupations.includes(item.listItemDescription)) {
        priorityItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    priorityItems.sort((a, b) => {
      return (
        prioritizedOccupations.indexOf(a.listItemDescription) -
        prioritizedOccupations.indexOf(b.listItemDescription)
      );
    });

    return [...priorityItems, ...otherItems];
  };

  const convertArrayFormat = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }

    const prioritizedArray = prioritizeOccupations(array);
    return prioritizedArray.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [
          martialData,
          occupationData,
          empBusinessData,
          empData,
        ] = await Promise.all([
          fetchMartialStatus(),
          fetchOccupation(),
          fetchEmpBusiness(),
          fetchEmp(),
        ]);

        setMartial(martialData);
        setOccupation(occupationData);
        setEmployeeBusiness(empBusinessData);
        setEmployee(empData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (watch("driverOccupation")?.value === "D34") {
      setValue("BusinessOccupation", { value: "703", label: "" });
    }
  }, [watch("driverOccupation")]);

  // done to retrigger the date validation, react-hook-form's known fault.
  let datePassedTest = watch("date");
  useEffect(() => {
    if (datePassedTest) trigger("date");
  }, [datePassedTest]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ3_ENTERED);
  }, []);

  const onSubmit = (data) => {
    Tq3Mapper.set(data);
    if (watch("date") !== null && watch("date") !== "Invalid Date") {
      if (isValid) {
        TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ3_NEXT_CLICKED);
      }
      handleNext(QuoteStep.TQ4);
      STATE.setHideTq2Questions(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <QuoteStepHeading
            title="Cover Details"
            description="Please provide your vehicle information."
          />

          <FieldStack className="tw-text-sm">
            <NewDatePicker
              name="date"
              label="When would you like your cover to start?"
              errors={errors.date}
              setValue={setValue}
              control={control}
              rules={{
                required: "Date is required",
              }}
              watch={watch}
              minDate={new Date()}
              maxDate={new Date(new Date().setDate(new Date().getDate() + 29))}
              onMaxDateExceeded={() => setIsDateLimitModalOpen(true)}
              trigger={trigger}
            />

            <LabelInput
              id="driverMaritalStatus"
              label="Marital status"
              type="select"
              options={convertArrayFormat(martial)}
              control={control}
              placeholder="Select martial status"
              register={register("driverMaritalStatus", { required: true })}
              watch={watch("driverMaritalStatus")}
              trigger={trigger}
              isLoading={isLoading}
              errors={errors.driverMaritalStatus}
            />

            {STATE.hideTq2Questions == false && (
              <>
                <LabelInput
                  id="driverOccupation"
                  label="Occupation"
                  type="select"
                  options={convertArrayFormat(occupation)}
                  control={control}
                  placeholder="Select occupation"
                  register={register("driverOccupation", { required: true })}
                  errors={errors.driverOccupation}
                  watch={watch("driverOccupation")}
                  trigger={trigger}
                  isSearchable={true}
                  isLoading={isLoading}
                />

                <LabelInput
                  id="BusinessOccupation"
                  label="Business type"
                  type="select"
                  options={convertArrayFormat(employeeBusiness)}
                  control={control}
                  placeholder="Select business occupation"
                  register={register("BusinessOccupation", { required: true })}
                  errors={errors.BusinessOccupation}
                  watch={watch("BusinessOccupation")}
                  trigger={trigger}
                  isSearchable={true}
                  isLoading={isLoading}
                />
                <LabelInput
                  id="empStatus"
                  label="Employee status "
                  type="select"
                  options={convertArrayFormat(employee)}
                  control={control}
                  placeholder="Select status"
                  register={register("empStatus", { required: true })}
                  errors={errors.empStatus}
                  watch={watch("empStatus")}
                  trigger={trigger}
                  isLoading={isLoading}
                />
              </>
            )}

            <RadioGroup
              name="DriverPartTime"
              mainLabel="Do you have part-time occupation?"
              register={register}
              trigger={trigger}
              errors={errors.DriverPartTime}
            />

            {watch("DriverPartTime") === "Yes" && (
              <>
                <LabelInput
                  id="partTimeOccup"
                  label="Part time occupation"
                  type="select"
                  options={convertArrayFormat(occupation)}
                  control={control}
                  placeholder="Select part time occupation"
                  register={register("partTimeOccup", {
                    required: true,
                  })}
                  watch={watch("partTimeOccup")}
                  trigger={trigger}
                  isSearchable={true}
                />
                {errors.partTimeOccup && (
                  <span className="tw-block tw-text-sm tw-text-dis-error">Required</span>
                )}

                <LabelInput
                  id="partTimeBusinessOccupation"
                  label="Part time business type"
                  type="select"
                  options={convertArrayFormat(employeeBusiness)}
                  control={control}
                  placeholder="Select part time business occupation"
                  register={register("partTimeBusinessOccupation", {
                    required: true,
                  })}
                  watch={watch("partTimeBusinessOccupation")}
                  trigger={trigger}
                  isSearchable={true}
                />
                {errors.partTimeBusinessOccupation && (
                  <span className="tw-block tw-text-sm tw-text-dis-error">Required</span>
                )}

                <LabelInput
                  id="parttimeempStatus"
                  label="Part time employee status "
                  type="select"
                  options={convertArrayFormat(employee)}
                  control={control}
                  placeholder="Select part time employee status"
                  register={register("parttimeempStatus", { required: true })}
                  watch={watch("parttimeempStatus")}
                  trigger={trigger}
                />
                {errors.parttimeempStatus && (
                  <span className="tw-block tw-text-sm tw-text-dis-error">Required</span>
                )}
              </>
            )}

            <RadioGroup
              name="DriverResidecy"
              mainLabel="Have you been resident in the UK since birth?"
              register={register}
              trigger={trigger}
              errors={errors.DriverResidecy}
              onNo={() => {
                setValue("DriverYears", null);
              }}
            />

            {watch("DriverResidecy") === "No" && (
              <LabelInput
                id="DriverYears"
                label="How many years have you been resident in the UK?"
                type="number"
                placeholder="Enter years"
                register={register("DriverYears", {
                  required: "Required",
                  pattern: {
                    value: /^\d{1,2}$/,
                    message: "The maximum number of digits is 2",
                  },
                })}
                errors={errors.DriverYears}
                watch={watch("DriverYears")}
                pattern="[0-9]*"
                trigger={trigger}
              />
            )}
          </FieldStack>
        </QuoteFormWrapper>
        <FormNavigationButtons
          prevStep={QuoteStep.TQ2}
          isValid={
            isValid &&
            watch("date") !== null &&
            watch("date") !== "Invalid Date"
          }
        />
      </form>
      {isDateLimitModalOpen && (
        <ModalWrapper
          heading="Date not available"
          zIndex={1400}
          simpleText="You can only select a date for a quote up to 30 days in advance."
          buttonText="OK"
          onClose={() => setIsDateLimitModalOpen(false)}
          onButtonClick={() => setIsDateLimitModalOpen(false)}
        />
      )}
    </>
  );
};

export default Tq3;
